/* eslint-disable max-lines */
import { Pipette, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCompositionsList } from "@design-lab/entities/composition";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui";
import { hexToHsl, hslToHex } from "@design-lab/widgets/artboard/lib/colorUtils";

import { ColorComboBox } from "./ColorComboBox";
import { ColorGradientPicker } from "./ColorGradientPicker";
import { CSS_COLORS } from "./cssColors";

type ColorPickerPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  onColorSelect: (color: string) => void;
  selectedColor?: string | undefined;
};

export function ColorPickerPopup({
  isOpen,
  onClose,
  onColorSelect,
  selectedColor = "#ffffff"
}: ColorPickerPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useResponsive();
  const { compositions } = useCompositionsList();

  const {
    h: hue,
    s: saturation,
    l: lightness
  } = useMemo(() => hexToHsl(selectedColor), [selectedColor]);

  const [colorInput, setColorInput] = useState(() => {
    // Find if current color matches a CSS color name
    const matchingColor = CSS_COLORS.find(c => c.hex.toLowerCase() === selectedColor.toLowerCase());
    return matchingColor ? matchingColor.name : selectedColor;
  });

  // Update colorInput when selectedColor changes
  useEffect(() => {
    const matchingColor = CSS_COLORS.find(c => c.hex.toLowerCase() === selectedColor.toLowerCase());
    setColorInput(matchingColor ? matchingColor.name : selectedColor);
  }, [selectedColor]);

  const colors = useMemo(() => {
    // Create a map of colors to avoid duplicates
    const colorMap = new Map<string, { id: string; name: string; hex: string }>();

    compositions.forEach(comp => {
      const key = comp.colorName;
      if (!colorMap.has(key)) {
        colorMap.set(key, {
          id: comp.id,
          name: comp.colorName,
          hex: comp.colorHex
        });
      }
    });

    return Array.from(colorMap.values());
  }, [compositions]);

  const updateColorFromHSL = useCallback(
    (h: number, s: number, l: number) => {
      const hex = hslToHex(h, s, l);
      onColorSelect(hex);
    },
    [onColorSelect]
  );

  const handleTShirtColorSelect = useCallback(
    (color: string) => {
      onColorSelect(color);
    },
    [onColorSelect]
  );

  const handleColorInputChange = useCallback(
    (value: string) => {
      setColorInput(value);

      // Check if it's a valid hex code
      if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
        onColorSelect(value);
        return;
      }

      // Check if it's a CSS color name (case-insensitive)
      const matchingColor = CSS_COLORS.find(c => c.name.toLowerCase() === value.toLowerCase());
      if (matchingColor) {
        onColorSelect(matchingColor.hex);
      }
    },
    [onColorSelect]
  );

  const handleEyeDropper = useCallback(async () => {
    if ("EyeDropper" in window) {
      // Close the popup to allow user to see the entire image
      onClose();

      try {
        const eyeDropper = new (window as any).EyeDropper();
        const result = await eyeDropper.open();
        onColorSelect(result.sRGBHex);
      } catch (e) {
        // User cancelled
      }
    }
  }, [onColorSelect, onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDesktop && popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen && isDesktop) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isDesktop]);

  if (!isOpen) return null;

  const defaultColors = (
    <div className="flex flex-col">
      <h3
        className={cn("font-semibold text-gray-500", isDesktop ? "mb-2 text-xs" : "mb-3 text-sm")}
      >
        Your T-shirt Colors
      </h3>
      <div
        className={cn(
          "flex flex-wrap overflow-y-auto p-1",
          isDesktop ? "max-h-[200px] gap-1.5" : "max-h-[300px] gap-2"
        )}
      >
        {colors.map(color => {
          const isSelected = selectedColor === color.hex;

          return (
            <button
              key={color.id}
              onClick={() => handleTShirtColorSelect(color.hex)}
              className={cn(
                "size-8 shrink-0 cursor-pointer rounded-full border transition-all hover:scale-105",
                isSelected ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300"
              )}
              style={{ backgroundColor: color.hex }}
              title={color.name}
              data-testid={`color-picker-tshirt-${color.id}`}
            />
          );
        })}
      </div>
    </div>
  );

  if (!isDesktop) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="translate-none bottom-0 left-0 top-auto flex h-auto max-h-[90vh] w-full flex-col rounded-b-none"
        closeOnBackdropClick={true}
        closeOnEsc={true}
      >
        <ModalHeader onClose={onClose}>Pick Color for Removal</ModalHeader>
        <ModalBody className="flex-1 space-y-0 p-4">
          <div className="mb-4 flex shrink-0 items-center gap-2">
            <ColorComboBox
              value={colorInput}
              onChange={handleColorInputChange}
              placeholder="white"
              size="lg"
              className="flex-1"
            />
            {"EyeDropper" in window && (
              <Button
                variant="outline"
                size="sm"
                onPress={handleEyeDropper}
                className="h-10 w-10 p-2"
                data-testid="color-picker-eyedropper"
              >
                <Pipette className="size-5" />
              </Button>
            )}
          </div>
          <ColorGradientPicker
            hue={hue}
            saturation={saturation}
            lightness={lightness}
            currentColor={selectedColor}
            onColorChange={updateColorFromHSL}
          />
          {defaultColors}
        </ModalBody>
      </Modal>
    );
  }

  return (
    <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2">
      <div
        ref={popupRef}
        className="flex h-[350px] w-[350px] max-w-[90vw] flex-col rounded-lg bg-white p-4 shadow-xl"
      >
        <div className="mb-3 flex shrink-0 items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Pick Color for Removal</h2>
          <Button
            variant="icon"
            size="sm"
            onPress={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="color-picker-close"
          >
            <X className="size-5" />
          </Button>
        </div>

        <div className="mb-3 flex shrink-0 items-center gap-2">
          <ColorComboBox
            value={colorInput}
            onChange={handleColorInputChange}
            placeholder="white"
            size="sm"
            className="flex-1"
          />
          {"EyeDropper" in window && (
            <Button
              variant="outline"
              size="sm"
              onPress={handleEyeDropper}
              className="h-8 w-8 p-1.5"
              data-testid="color-picker-eyedropper"
            >
              <Pipette className="size-5" />
            </Button>
          )}
        </div>

        <ColorGradientPicker
          hue={hue}
          saturation={saturation}
          lightness={lightness}
          currentColor={selectedColor}
          onColorChange={updateColorFromHSL}
        />
        {defaultColors}
      </div>
    </div>
  );
}
